import { ReactNode, useCallback, KeyboardEvent } from "react";

import useTypedContext from "hooks/useTypedContext";
import { TreeNode } from "utils/tree";
import Tree, { TreeBranch, TreeBranchLink } from "ds/components/Tree";

import { TreeListItemWithCheckboxProps } from "../../types";
import { TreeListContext } from "../../Context";

type TreeListNodeMultiSelectProps<T extends object> = {
  node: TreeNode<T>;
  renderItem: (option: TreeListItemWithCheckboxProps<T>) => ReactNode;
  isRoot?: boolean;
  selectedKeys: Set<string>;
  onCheckboxChange: (id: string, checked: boolean) => void;
  getIndeterminateState: (id: string) => boolean;
  defaultSelection?: string;
};

const TreeListNodeMultiSelect = <T extends object>({
  node,
  renderItem,
  isRoot,
  selectedKeys,
  onCheckboxChange,
  getIndeterminateState,
  defaultSelection,
}: TreeListNodeMultiSelectProps<T>) => {
  const {
    expandedKeys,
    renderedKeys,
    disabledKeys,
    toggleKey,
    searchQuery,
    onFocus,
    onKeyDown,
    selectedRef,
    focusedKey,
  } = useTypedContext(TreeListContext);

  const isExpanded = expandedKeys.has(node.id);
  const isRendered = renderedKeys.has(node.id);
  const isDisabled = disabledKeys?.includes(node.id);
  const isChecked = selectedKeys.has(node.id);
  const isIndeterminate = getIndeterminateState(node.id);
  const isOrigin = defaultSelection === node.id;

  const handleToggle = useCallback(() => {
    toggleKey(node.id);
  }, [node.id, toggleKey]);

  const handleCheckboxChange = useCallback(
    (checked: boolean) => {
      onCheckboxChange(node.id, checked);
    },
    [node.id, onCheckboxChange]
  );

  const handleFocus = useCallback(() => {
    onFocus(node.id);
  }, [node.id, onFocus]);

  const handleBlur = useCallback(() => {
    onFocus("");
  }, [onFocus]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      onKeyDown(e, node.id);
    },
    [node.id, onKeyDown]
  );

  const hasChildren = node.children.some((child) => renderedKeys.has(child.id));

  if (!isRendered) {
    return null;
  }

  return (
    <TreeBranch direction="column" isFirst={isRoot}>
      <TreeBranchLink>
        {renderItem({
          item: node.item,
          id: node.id,
          name: node.name,
          isExpanded: hasChildren ? isExpanded : undefined,
          isDisabled,
          searchQuery,
          innerRef: selectedRef,
          hasFocus: node.id === focusedKey,
          isChecked,
          isIndeterminate,
          onToggle: hasChildren ? handleToggle : undefined,
          onCheckboxChange: handleCheckboxChange,
          onFocus: handleFocus,
          onBlur: handleBlur,
          onKeyDown: handleKeyDown,
          isOrigin,
        })}
      </TreeBranchLink>
      {hasChildren && isExpanded && (
        <Tree direction="column" fullWidth={false} role="group">
          {node.children.map((child) => (
            <TreeListNodeMultiSelect
              key={child.id}
              node={child}
              renderItem={renderItem}
              selectedKeys={selectedKeys}
              onCheckboxChange={onCheckboxChange}
              getIndeterminateState={getIndeterminateState}
              defaultSelection={defaultSelection}
            />
          ))}
        </Tree>
      )}
    </TreeBranch>
  );
};

TreeListNodeMultiSelect.displayName = "DS.TreeList.MultiSelect.Node";

export default TreeListNodeMultiSelect;
